import "./Notifications.scss";
import React, { useState, useEffect, useRef, useCallback } from "react";
import DeleteIcon from "./../../assets/images/delete-icon.png";
import Loader from "../../components/Loader/Loader";
import api from "../../utils/api";
import { useLanguage } from "../../context/LanguageContext";
import { useWebSocket } from "../../context/WebSocketContext";
import { photosApi } from "../../SourceData";
import { toast } from "react-toastify";

const Notifications = () => {
  const { language, t, dir } = useLanguage();
  const { notifications: wsNotifications } = useWebSocket();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const observer = useRef();

  // Function to fetch notifications
  const fetchNotifications = async (pageNum) => {
    try {
      setIsLoading(true);
      const response = await api.get(
        `/notifications/me?page=${pageNum}&limit=5`
      );

      const { data, totalPages } = response.data;

      // Process the notifications to extract the correct language content
      const processedNotifications = data.map((notification) => ({
        id: notification._id,
        code: notification._id?.substring(0, 8),
        title:
          notification.title && notification.title[language]
            ? notification.title[language]
            : "",
        text:
          notification.text && notification.text[language]
            ? notification.text[language]
            : "",
        time: new Date(notification.createdAt).toLocaleString(),
        photo: notification.photo,
        isRead: notification.isReaded || false, // Use isReaded from API response
      }));

      if (pageNum === 1) {
        setNotifications(processedNotifications || []);
      } else {
        setNotifications((prev) => [
          ...prev,
          ...(processedNotifications || []),
        ]);
      }

      setHasMore(pageNum < totalPages);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchNotifications(1);
  }, [language]);

  // Handle websocket notifications
  useEffect(() => {
    if (wsNotifications && wsNotifications.length > 0) {
      // Process and add new websocket notifications
      const newNotifications = wsNotifications.map((notification) => {
        // Handle both object and string formats for title/text
        const title =
          typeof notification.title === "object"
            ? notification.title[language] || ""
            : notification.title || "";

        const text =
          typeof notification.text === "object"
            ? notification.text[language] || ""
            : notification.text || notification.message || "";

        return {
          id: notification._id || notification.id,
          code: (notification._id || notification.id)?.substring(0, 8),
          title: title,
          text: text,
          time: new Date(
            notification.createdAt || notification.timestamp || Date.now()
          ).toLocaleString(),
          photo: notification.photo,
          isRead: notification.isReaded || false,
          isRealTime: true,
        };
      });

      // Check for duplicates and add new notifications at the top
      setNotifications((prevNotifications) => {
        const existingIds = prevNotifications.map((n) => n.id);
        const filteredNew = newNotifications.filter(
          (n) => !existingIds.includes(n.id)
        );
        return [...filteredNew, ...prevNotifications];
      });
    }
  }, [wsNotifications, language]);

  // Load more notifications when scrolling
  const loadMoreNotifications = () => {
    if (!isLoading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNotifications(nextPage);
    }
  };

  // Setup the intersection observer for infinite scroll
  const lastNotificationRef = useCallback(
    (node) => {
      if (isLoading) return;

      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreNotifications();
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [isLoading, hasMore]
  );

  // Handle delete notification with updated route
  const handleDeleteNotification = async (id) => {
    try {
      // Call API to delete the notification with new route
      await api.patch(`/notifications/${id}/delete`);

      // Update the UI by removing the deleted notification
      setNotifications(
        notifications.filter((notification) => notification.id !== id)
      );
      toast.success(t("notificationDeleted"));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  // Handle mark notification as read
  const handleMarkAsRead = async (id) => {
    try {
      // Call API to mark notification as read
      await api.patch(`/notifications/${id}`);

      // Update the UI by marking the notification as read
      setNotifications(
        notifications.map((notification) =>
          notification.id === id
            ? { ...notification, isRead: true }
            : notification
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Get translation for mark as read
  const getMarkAsReadText = () => {
    return language === "ar" ? "تحديد كمقروء" : "Mark as read";
  };

  // Render a single notification item
  const renderNotificationItem = (notification, index) => {
    const isLastItem = index === notifications.length - 1;

    return (
      <div
        key={notification.id}
        ref={isLastItem ? lastNotificationRef : null}
        className={`notification-item ${
          notification.isRead ? "read" : "unread"
        } ${notification.isRealTime ? "real-time" : ""}`}
      >
        <div className="right">
          <p className="item-code">{notification.title}</p>
          <p className="item-text">{notification.text}</p>
          {notification.photo && (
            <img
              className="item-img"
              src={photosApi + notification.photo}
              alt={notification.title || "Notification"}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}
          <p className="item-time">{notification.time}</p>

          {/* Mark as read button - only show for unread notifications */}
          {!notification.isRead && (
            <button
              className="mark-as-read-btn"
              onClick={() => handleMarkAsRead(notification.id)}
            >
              {getMarkAsReadText()}
            </button>
          )}
        </div>
        <div className="left">
          <img
            src={DeleteIcon}
            alt={t("delete")}
            onClick={() => handleDeleteNotification(notification.id)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="notifications" dir={dir}>
      <div className="container">
        <h2 className="section-title">{t("notifications")}</h2>

        {notifications.length > 0 ? (
          <div className="notifications-container">
            {notifications.map((notification, index) =>
              renderNotificationItem(notification, index)
            )}
            {isLoading && page > 1 && (
              <div className="loading-more">
                <Loader text={t("loading")} />
              </div>
            )}
          </div>
        ) : isLoading && page === 1 ? (
          <Loader text={t("loading")} />
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🔔</div>
            <h3 className="empty-title">{t("noNotifications")}</h3>
            <p className="empty-description">
              {t("noNotificationsDescription") ||
                "ليس لديك أي إشعارات جديدة حالياً. ستظهر هنا الإشعارات المتعلقة بطلباتك واشتراكاتك."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
